import { isPast } from 'core/utils/date'
import { type ObjectValues } from 'core/utils/object'

import { type Accessorial, type PriceItemTypeWithNotes } from './Accessorial'
import { type Carrier } from './Carrier'
import { getLanePerProposalPreference, isLaneAwardedConfirmed, type Lane } from './Lane'
import { hasValidAwardedType } from './LaneProposal'
import { type ProposalState } from './ProposalState'
import { type RateType } from './RateType'
import {
  isRfpAwarding,
  isRfpDeleted,
  isRfpExpired,
  isRfpFinalized,
  type RequestForProposal,
} from './RequestForProposal'

export type AutoSubmit = {
  autoSubmit: boolean
}

export type AwardConfirmationAutoSubmit = {
  awardConfirmationAutoSubmit: boolean
}

export const ProposalSubmitState = {
  Open: 'open',
  Closed: 'closed',
  Submitted: 'submitted',
  Negotiating: 'negotiating',
} as const

export type ProposalSubmitStateType = ObjectValues<typeof ProposalSubmitState>

export type BackgroundProcessErrors = {
  message?: string
  details?: unknown
}

export type BackgroundProcess = {
  process?: 'upload' | 'submit'
  result?: 'success' | 'failure'
  status?: 'running' | 'finished'
  errors?: BackgroundProcessErrors
}

export type Proposal = {
  accessorials: Array<Accessorial>
  requestedAccessorials: Array<PriceItemTypeWithNotes>
  awardConfirmationAutoSubmit: boolean
  awardConfirmationDeadline?: Date
  awardFeedback?: string
  carrier: Carrier
  inviteUnderNegotiation: boolean
  lanes: Array<Lane>
  rateType: RateType
  rfp: RequestForProposal
  shipperCompanyName: string
  state: ProposalState
  submittedAt?: Date
  suggestSignup: boolean
  unsubscribed: boolean
  backgroundProcess?: BackgroundProcess
} & AutoSubmit

export type ProposalStep = 'bid' | 'award'

export function canReceiveProposals(proposal?: Proposal): boolean {
  return Boolean(proposal?.rfp?.canReceiveProposals)
}

export function canSubmitRates(proposal?: Proposal): boolean {
  if (!proposal) return false

  const state = getProposalSubmitState(proposal)

  return state === ProposalSubmitState.Open || state === ProposalSubmitState.Negotiating
}

export function canUpdateRates(proposal?: Proposal): boolean {
  if (!proposal) return false

  if (isRfpDeleted(proposal.rfp)) return false
  if (!isProposalStateAllowed(proposal)) return false
  if (isProposalSubmitted(proposal)) return false
  if (hasRunningProcess(proposal)) return false

  return canReceiveProposals(proposal)
}

export function getAwardedConfirmationLanes({ lanes }: Proposal) {
  const lanesWithProposals = getLanePerProposalPreference(lanes)

  const acceptedLanes = lanesWithProposals.filter(
    ({ laneProposal }) => laneProposal?.awardConfirmation === 'accepted'
  )
  const rejectedLanes = lanesWithProposals.filter(
    ({ laneProposal }) =>
      laneProposal?.awardConfirmation === 'denied' || laneProposal?.awardConfirmation === undefined
  )

  return {
    acceptedLanes,
    rejectedLanes,
  }
}

export function getAwardedConfirmationLanesCount({ lanes }: Proposal) {
  const acceptedCount = lanes.filter(l => l.laneProposal?.awardConfirmation === 'accepted').length
  const rejectedCount = lanes.filter(l => l.laneProposal?.awardConfirmation === 'denied').length
  const pendingCount = lanes.filter(l => l.laneProposal?.awardConfirmation === undefined).length

  return {
    acceptedCount,
    rejectedCount,
    pendingCount,
  }
}

export function getAwardedLaneIds({ lanes }: Proposal) {
  return lanes.map(lane => lane.laneProposals!.map(proposal => proposal.id)).flat()
}

export function getConfirmedAwardedLanes({ lanes }: Proposal) {
  return lanes.filter(({ laneProposal }) => {
    return laneProposal ? laneProposal.awardConfirmation : false
  })
}

export function getProposalSubmitState(proposal?: Proposal): ProposalSubmitStateType {
  if (!proposal) return ProposalSubmitState.Closed

  if (isRfpDeleted(proposal.rfp) || !isProposalStateAllowed(proposal))
    return ProposalSubmitState.Closed

  if (isProposalUnderNegotiation(proposal)) return ProposalSubmitState.Negotiating

  if (canReceiveProposals(proposal)) {
    if (isProposalSubmitted(proposal)) return ProposalSubmitState.Submitted

    return ProposalSubmitState.Open
  }

  return ProposalSubmitState.Closed
}

export function hasConfirmedAwardedLanes({ lanes }: Proposal): boolean {
  return lanes.some(isLaneAwardedConfirmed)
}

export function hasShipperInformation({
  rfp: { shipperEmail, shipperName, shipperPhone },
}: Proposal): boolean {
  return Boolean(shipperEmail) || Boolean(shipperName) || Boolean(shipperPhone)
}

export function isProposalAwarded(proposal?: Proposal): boolean {
  if (!proposal) return false
  if (!isRfpAwarding(proposal.rfp)) return false

  const awardedLane = proposal?.lanes.find(({ laneProposal }) =>
    hasValidAwardedType(laneProposal?.awardedType)
  )

  return Boolean(awardedLane)
}

export function isProposalAwardExpired(proposal?: Proposal): boolean {
  if (!proposal || !proposal.awardConfirmationDeadline) return false

  return isPast(proposal.awardConfirmationDeadline)
}

export function isProposalAwardFinished(proposal?: Proposal): boolean {
  if (!proposal) return false
  return proposal.state === 'award_finished'
}

export function isProposalClosed(proposal?: Proposal): boolean {
  if (!proposal) return true

  const isExpired = isProposalExpired(proposal)
  const isFinalized = isProposalFinalized(proposal)
  const cannotReceiveProposals = !canReceiveProposals(proposal)
  const isDeleted = isProposalDeleted(proposal)

  return isExpired || isFinalized || cannotReceiveProposals || isDeleted
}

export function isProposalDeleted(proposal?: Proposal): boolean {
  if (!proposal) return true
  return isRfpDeleted(proposal.rfp)
}

export function isProposalExpired(proposal?: Proposal): boolean {
  if (!proposal) return true
  return isRfpExpired(proposal.rfp)
}

export function isProposalFinalized(proposal?: Proposal): boolean {
  if (!proposal) return true
  return isRfpFinalized(proposal.rfp)
}

export function isProposalStateAllowed(proposal?: Proposal): boolean {
  if (!proposal) return false

  return ['published', 'closed'].includes(proposal.rfp.state)
}

export function isProposalSubmitted(proposal?: Proposal): boolean {
  if (!proposal) return false
  return proposal.state === 'submitted'
}

export function isProposalUnderNegotiation(proposal?: Proposal): boolean {
  if (!proposal) return false

  return proposal.inviteUnderNegotiation
}

export function getRateFileAnalysis(proposal?: Proposal) {
  if (!proposal) {
    return {
      analyzingRateFile: false,
      rateFileAnalysisFail: false,
      rateFileAnalysisSuccess: false,
    }
  }

  const { backgroundProcess } = proposal

  const isUpload = backgroundProcess?.process === 'upload'
  const isRunning = backgroundProcess?.status === 'running'
  const isFinished = backgroundProcess?.status === 'finished'
  const hasFailed = backgroundProcess?.result === 'failure'
  const hasSucceed = backgroundProcess?.result === 'success'

  return {
    analyzingRateFile: isUpload && isRunning,
    rateFileAnalysisFail: isUpload && isFinished && hasFailed,
    rateFileAnalysisSuccess: isUpload && isFinished && hasSucceed,
  }
}

export function getSubmitAnalysis(proposal?: Proposal) {
  if (!proposal) {
    return {
      submitting: false,
      submitFail: false,
      submitSuccess: false,
    }
  }

  const { backgroundProcess } = proposal

  const isSubmit = backgroundProcess?.process === 'submit'
  const isRunning = backgroundProcess?.status === 'running'
  const isFinished = backgroundProcess?.status === 'finished'
  const hasFailed = backgroundProcess?.result === 'failure'
  const hasSucceed = backgroundProcess?.result === 'success'

  return {
    submitting: isSubmit && isRunning,
    submitFail: isSubmit && isFinished && hasFailed,
    submitSuccess: isSubmit && isFinished && hasSucceed,
  }
}

export function hasRunningProcess(proposal?: Proposal): boolean {
  if (!proposal) return false

  return proposal.backgroundProcess?.status === 'running'
}
